import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';
import { ILightWeightBusinessUnit, ILightWeightObject } from '@fi-shared/models';
import { FiProfileSizes } from '@fi-shared/profile-icon/profile-icon.component';
import { Animations } from '@fi-shared/utils/animations';

@Component({
    selector: 'fi-task-v2-role-unit-assignee',
    templateUrl: './task-v2-role-unit-assignee.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-task-v2-role-unit-assignee' },
    animations: [Animations.fadeHeight()],
    standalone: false,
})
export class TaskV2RoleUnitAssigneeComponent {
    @Input({ required: true }) roles: ILightWeightObject[] = [];
    @Input({ required: true }) businessUnits: ILightWeightBusinessUnit[] = [];
    @Input({ required: true }) accessToPreferredBusinessUnitMode: boolean;
    @Input() maxDisplayedItems = 4;
    @Input() staffMemberCount = 0;

    @Output() businessUnitClicked = new EventEmitter<number>();
    @Output() showStaffMembersClicked = new EventEmitter<void>();
    @Output() showAllChanged = new EventEmitter<boolean>();

    protected showAll = false;
    protected readonly FiProfileSizes = FiProfileSizes;

    protected get hasMoreRolesOrUnits(): boolean {
        const totalCount = (this.roles?.length || 0) + (this.businessUnits?.length || 0);
        return totalCount > this.maxDisplayedItems;
    }

    protected get additionalRolesAndUnitsCount(): number {
        const totalCount = (this.roles?.length || 0) + (this.businessUnits?.length || 0);
        return Math.max(0, totalCount - this.maxDisplayedItems);
    }

    protected get currentRoles(): ILightWeightObject[] {
        if (!this.roles) {
            return [];
        }
        return this.showAll ? this.roles : this.getTrimmedRoles();
    }

    protected get currentBusinessUnits(): ILightWeightBusinessUnit[] {
        if (!this.businessUnits) {
            return [];
        }
        return this.showAll ? this.businessUnits : this.getTrimmedBusinessUnits();
    }

    protected toggleShowAllRolesAndUnits(): void {
        this.showAll = !this.showAll;
        this.showAllChanged.emit(this.showAll);
    }

    protected openBusinessUnitDetails(businessUnitId: number): void {
        this.businessUnitClicked.emit(businessUnitId);
    }

    protected openAssignedStaffMembersViewer(): void {
        this.showStaffMembersClicked.emit();
    }

    private getTrimmedRoles(): ILightWeightObject[] {
        if (!this.roles || this.roles.length === 0) {
            return [];
        }
        return this.roles.slice(0, this.maxDisplayedItems);
    }

    private getTrimmedBusinessUnits(): ILightWeightBusinessUnit[] {
        if (!this.businessUnits || this.businessUnits.length === 0) {
            return [];
        }
        const availableSlots = this.maxDisplayedItems - (this.roles?.length || 0);
        return availableSlots > 0 ? this.businessUnits.slice(0, availableSlots) : [];
    }
}
